import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Paper from "@mui/material/Paper";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import { CreateNewFolder as CreatePortfolioIcon } from "@mui/icons-material";

import Portfolio from "./Portfolio";
import StockScreener from "./StockScreener";
import PortfolioInsights from "./PortfolioInsights";
import Finviz from "./Finviz";
import SectorCardView from "./SectorCardView";
import PerformanceView from "./PerformanceView";
import ScreenerCombinedView from "./ScreenerCombinedView";
import ScreenerTopPerformanceCharts from "./ScreenerTopPerformanceCharts";
import Utils from "../util/Utils";
import { API_KEYS, FMP_API_ENDPOINTS } from "../util/Constants";

// --- Constants ---
const BASE_COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#AA00FF",
  "#FF00AA",
  "#82ca9d",
  "#FA8072",
];
const SECTOR_COLORS = [
  "#FF6384",
  "#36A2EB",
  "#FFCE56",
  "#4BC0C0",
  "#9966FF",
  "#FF9F40",
  "#C9CBCF",
  "#77DD77",
];

// --- Helper Functions ---
const lightenColor = (color, percent) => {
  let num = parseInt(color.replace("#", ""), 16),
    amt = Math.round(2.55 * percent),
    R = (num >> 16) + amt,
    B = ((num >> 8) & 0x00ff) + amt,
    G = (num & 0x0000ff) + amt;
  return (
    "#" +
    (
      0x1000000 +
      (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
      (B < 255 ? (B < 1 ? 0 : B) : 255) * 0x100 +
      (G < 255 ? (G < 1 ? 0 : G) : 255)
    )
      .toString(16)
      .slice(1)
  );
};

const formatDollarValue = (value, showSign = false) => {
  const number = Number(value);
  if (isNaN(number)) return "$0.00";
  const options = { style: "currency", currency: "USD" };
  let formatted = new Intl.NumberFormat("en-US", options).format(
    Math.abs(number)
  );
  if (number < 0) {
    formatted = `-${formatted.replace("$", "")}`;
  } else if (showSign && number > 0) {
    formatted = `+${formatted}`;
  }
  return formatted;
};

const RADIAN = Math.PI / 180;
const renderCustomizedLabelWithLines = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  percent,
  name,
  fill,
}) => {
  if (percent * 100 < 2) return null;
  const radius = outerRadius + 25;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 10) * Math.cos(-midAngle * RADIAN);
  const sy = cy + (outerRadius + 10) * Math.sin(-midAngle * RADIAN);
  const mx = cx + (outerRadius + 20) * Math.cos(-midAngle * RADIAN);
  const my = cy + (outerRadius + 20) * Math.sin(-midAngle * RADIAN);
  const ex = mx + (Math.cos(-midAngle * RADIAN) >= 0 ? 1 : -1) * 12;
  const ey = my;
  const textAnchor = Math.cos(-midAngle * RADIAN) >= 0 ? "start" : "end";
  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={fill}
        fill="none"
      />
      <circle cx={ex} cy={ey} r={2} fill={fill} stroke="none" />
      <text
        x={ex + (Math.cos(-midAngle * RADIAN) >= 0 ? 1 : -1) * 6}
        y={ey}
        textAnchor={textAnchor}
        fill="#333"
        dominantBaseline="central"
        fontSize="11px"
      >{`${name} (${(percent * 100).toFixed(0)}%)`}</text>
    </g>
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box
          sx={{
            p: 0,
            width: "calc(100% - 2rem)",
            margin: "0.5rem 1rem 0.25rem 1rem",
          }}
        >
          {children}
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function PortfolioContainer(props) {
  const {
    userEmail,
    userPortfolio: initialUserPortfolio,
    portfolioExists: initialPortfolioExists,
    showCreateUserPortfolioForm: initialShowCreateForm,
    newUserPortfolioNameInput: initialNewPortfolioName,
    onNewPortfolioNameInputChange,
    onCreateUserPortfolio,
    isLoading: globalIsLoading,
    error: globalError,
    onSaveStock,
    onDeleteStock,
    onRefreshPortfolio,
    portfolioFromHome, // This is the 'screenerPortfolio' from Home.js state, used as a fallback
    portfolioImageUrlFromHome,
    snapShotFnFromHome,
    isMobile,
  } = props;

  const [value, setValue] = useState(0);

  // New state for real-time prices
  const [realTimePrices, setRealTimePrices] = useState({});
  const [isFetchingPrices, setIsFetchingPrices] = useState(false);
  const [priceFetchError, setPriceFetchError] = useState(null);

  const userPortfolio = initialUserPortfolio;
  const portfolioExists = initialPortfolioExists;
  const showCreateUserPortfolioForm = initialShowCreateForm;
  const newUserPortfolioNameInput = initialNewPortfolioName;
  const isLoading = globalIsLoading;
  const error = globalError;

  // Fetch real-time prices for all stocks in the portfolio
  useEffect(() => {
    async function fetchPrices() {
      if (
        !userPortfolio ||
        !userPortfolio.stocks ||
        userPortfolio.stocks.length === 0
      ) {
        setRealTimePrices({});
        return;
      }
      setIsFetchingPrices(true);
      setPriceFetchError(null);
      try {
        const symbols = userPortfolio.stocks
          .map((s) => s.symbol)
          .filter(Boolean);
        if (symbols.length === 0) {
          setRealTimePrices({});
          setIsFetchingPrices(false);
          return;
        }
        const url = `${FMP_API_ENDPOINTS.QUOTE(symbols.join(","))}?${
          API_KEYS.FMP_API_KEY
        }`;
        const response = await fetch(url);
        if (!response.ok)
          throw new Error(`Failed to fetch prices: ${response.status}`);
        const data = await response.json();
        // Map: symbol -> { price, previousClose }
        const priceMap = {};
        data.forEach((item) => {
          if (item.symbol && item.price != null) {
            priceMap[item.symbol.toUpperCase()] = {
              price: item.price,
              previousClose: item.previousClose,
            };
          }
        });
        setRealTimePrices(priceMap);
      } catch (err) {
        setPriceFetchError(err.message);
        setRealTimePrices({});
      } finally {
        setIsFetchingPrices(false);
      }
    }
    fetchPrices();
  }, [userPortfolio]);

  // Helper to get the real-time price and previous close for a stock
  const getCurrentPrice = (symbol) => {
    if (!symbol) return 0;
    return realTimePrices[symbol.toUpperCase()]?.price ?? 0;
  };
  const getPreviousClose = (symbol) => {
    if (!symbol) return undefined;
    return realTimePrices[symbol.toUpperCase()]?.previousClose;
  };

  // Use real-time prices in all calculations
  const portfolioStocksForTable = useMemo(
    () =>
      (userPortfolio?.stocks || []).map((stock) => {
        const currentPrice = getCurrentPrice(stock.symbol);
        const previousClose = getPreviousClose(stock.symbol);
        const quantity = parseFloat(stock.quantity) || 0;
        let todaysGain = undefined;
        let todaysGainPct = undefined;
        if (previousClose && previousClose !== 0) {
          todaysGain = (currentPrice - previousClose) * quantity;
          todaysGainPct =
            ((currentPrice - previousClose) / previousClose) * 100;
        }
        return {
          ...stock,
          currentPrice,
          previousClose,
          todaysGain,
          todaysGainPct,
        };
      }),
    [userPortfolio, realTimePrices]
  );

  // Updated to use userPortfolio.screenerPortfolio
  const screenerPortfolioStockReports = useMemo(() => {
    return userPortfolio?.screenerPortfolio?.stockReports || [];
  }, [userPortfolio]);

  const symbolDistributionDataForUserStocks = useMemo(() => {
    if (!portfolioStocksForTable || portfolioStocksForTable.length === 0)
      return [];
    return portfolioStocksForTable
      .map((stock) => {
        const quantity = parseFloat(stock.quantity) || 0;
        const currentPrice = parseFloat(stock.currentPrice) || 0;
        const marketValue = quantity * currentPrice;
        return {
          name: stock.symbol,
          value: parseFloat(marketValue.toFixed(2)),
        };
      })
      .filter((item) => item.value > 0);
  }, [portfolioStocksForTable]);

  const stockPerformanceDataForUserStocks = useMemo(() => {
    if (!portfolioStocksForTable || portfolioStocksForTable.length === 0)
      return [];
    return portfolioStocksForTable.map((stock) => {
      const quantity = parseFloat(stock.quantity) || 0;
      const costPerStock = parseFloat(stock.costPerStock) || 0;
      const currentPrice = parseFloat(stock.currentPrice) || 0;
      const costBasis = quantity * costPerStock;
      const marketValue = quantity * currentPrice;
      const gainLoss = marketValue - costBasis;
      const percentageGainLoss =
        costBasis !== 0 && !isNaN(costBasis) ? (gainLoss / costBasis) * 100 : 0;
      return { ...stock, gainLoss, percentageGainLoss };
    });
  }, [portfolioStocksForTable]);

  const topGainersDataForUserStocks = useMemo(() => {
    return [...stockPerformanceDataForUserStocks]
      .filter((stock) => stock.gainLoss > 0)
      .sort((a, b) => b.gainLoss - a.gainLoss)
      .slice(0, 5);
  }, [stockPerformanceDataForUserStocks]);

  const topLosersDataForUserStocks = useMemo(() => {
    return [...stockPerformanceDataForUserStocks]
      .filter((stock) => stock.gainLoss < 0)
      .sort((a, b) => a.gainLoss - b.gainLoss)
      .slice(0, 5);
  }, [stockPerformanceDataForUserStocks]);

  const sectorAllocationByMarketValueForUserStocks = useMemo(() => {
    if (
      !portfolioStocksForTable ||
      portfolioStocksForTable.length === 0 ||
      !userPortfolio?.screenerPortfolio?.stockReports
    )
      return [];

    // Create a map of symbol to sector from screenerPortfolio.stockReports
    const symbolToSectorMap =
      userPortfolio.screenerPortfolio.stockReports.reduce((acc, report) => {
        acc[report.symbol] = report.sector || "Unassigned";
        return acc;
      }, {});

    const allocation = portfolioStocksForTable.reduce((acc, stock) => {
      const quantity = parseFloat(stock.quantity) || 0;
      const currentPrice = parseFloat(stock.currentPrice) || 0;
      const marketValue = quantity * currentPrice;
      // Use sector from screenerPortfolio.stockReports, fallback to stock.sector if not found
      const sector =
        symbolToSectorMap[stock.symbol] || stock.sector || "Unassigned";
      acc[sector] = (acc[sector] || 0) + marketValue;
      return acc;
    }, {});

    return Object.entries(allocation)
      .map(([name, value]) => ({
        name,
        value: parseFloat(value.toFixed(2)),
      }))
      .filter((item) => item.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [portfolioStocksForTable, userPortfolio?.screenerPortfolio?.stockReports]);

  // Add the missing handleChange function for Tabs
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (isFetchingPrices) {
    return (
      <Container sx={{ textAlign: "center", mt: 4, p: 3 }}>
        <CircularProgress />
        <Typography>Loading Real-Time Prices...</Typography>
      </Container>
    );
  }

  if (priceFetchError) {
    return (
      <Container sx={{ textAlign: "center", mt: 4, p: 3 }}>
        <Alert severity="error">{priceFetchError}</Alert>
      </Container>
    );
  }

  if (isLoading && !userPortfolio && !showCreateUserPortfolioForm) {
    return (
      <Container sx={{ textAlign: "center", mt: 4, p: 3 }}>
        <CircularProgress />
        <Typography>Loading Portfolio Data...</Typography>
      </Container>
    );
  }

  if (showCreateUserPortfolioForm && !portfolioExists) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4, mb: 4, p: 3 }}>
        <Paper elevation={3} sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create Your Portfolio
          </Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box
            component="form"
            onSubmit={onCreateUserPortfolio}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              id="portfolioName"
              label="Portfolio Name"
              name="portfolioName"
              autoFocus
              value={newUserPortfolioNameInput}
              onChange={onNewPortfolioNameInputChange}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading}
              startIcon={<CreatePortfolioIcon />}
            >
              {isLoading ? <CircularProgress size={24} /> : "Create Portfolio"}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  if (!portfolioExists && !isLoading) {
    return (
      <Container sx={{ textAlign: "center", mt: 4, p: 3 }}>
        <Typography color="error">
          {error || "Could not load or find your portfolio."}
        </Typography>
      </Container>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <AppBar position="static" color="inherit">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="portfolio container tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ "& .MuiTabs-indicator": { backgroundColor: "primary.main" } }}
        >
          <Tab label="Portfolio" {...a11yProps(0)} />
          <Tab label="Holdings" {...a11yProps(1)} />
          <Tab label="SnapShots" {...a11yProps(2)} />
          <Tab label="Insights" {...a11yProps(3)} />
          <Tab label="Sector View" {...a11yProps(4)} />
          <Tab label="Performance View" {...a11yProps(5)} />
          <Tab label="Charts" {...a11yProps(6)} />
          <Tab label="Top 10 Stocks By Performance" {...a11yProps(7)} />
        </Tabs>
      </AppBar>

      <TabPanel value={value} index={0}>
        <Portfolio
          userPortfolio={userPortfolio}
          portfolioStocks={portfolioStocksForTable}
          isLoading={isLoading}
          error={error}
          onSaveStock={onSaveStock}
          onDeleteStock={onDeleteStock}
          onRefreshPortfolio={onRefreshPortfolio}
          isMobile={isMobile}
          showTodaysGainColumns={true}
        />
      </TabPanel>

      <TabPanel value={value} index={1}>
        <StockScreener
          // Use screenerPortfolio from the fetched userPortfolio for Holdings tab
          portfolio={userPortfolio?.screenerPortfolio || portfolioFromHome}
          portfolioImageUrl={portfolioImageUrlFromHome}
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <ScreenerCombinedView
            stockReports={screenerPortfolioStockReports}
            displayAll={false}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={3}>
        <PortfolioInsights
          name={
            userPortfolio?.screenerPortfolio?.name ||
            userPortfolio?.portfolioName ||
            "Portfolio Analysis"
          }
          // Data for screener-based insights
          portfolioAnalysisData={screenerPortfolioStockReports}
          // Data for insights based on userPortfolio.stocks (live data)
          symbolDistributionData={symbolDistributionDataForUserStocks}
          portfolioTopGainers={topGainersDataForUserStocks}
          portfolioTopLosers={topLosersDataForUserStocks}
          sectorAllocationByMarketValue={
            sectorAllocationByMarketValueForUserStocks
          }
          BASE_COLORS={BASE_COLORS}
          SECTOR_COLORS={SECTOR_COLORS}
          lightenColor={lightenColor}
          formatDollarValue={formatDollarValue}
          renderCustomizedLabelWithLines={renderCustomizedLabelWithLines}
        />
      </TabPanel>
      <TabPanel value={value} index={4}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <SectorCardView
            stockReports={screenerPortfolioStockReports}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={5}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <PerformanceView
            stockReports={screenerPortfolioStockReports}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={6}>
        <Box sx={{ width: { xs: "100%", md: "95%" }, mx: "auto", mt: 0 }}>
          <Finviz
            stockReports={screenerPortfolioStockReports}
            displayAll={false}
          />
        </Box>
      </TabPanel>
      <TabPanel value={value} index={7}>
        <Box sx={{ width: "100%", mx: 0, mt: 0 }}>
          <ScreenerTopPerformanceCharts
            stockReports={screenerPortfolioStockReports}
            topN={10}
          />
        </Box>
      </TabPanel>
    </Box>
  );
}

PortfolioContainer.propTypes = {
  userEmail: PropTypes.string.isRequired,
  userPortfolio: PropTypes.object,
  portfolioExists: PropTypes.bool,
  showCreateUserPortfolioForm: PropTypes.bool,
  newUserPortfolioNameInput: PropTypes.string,
  onNewPortfolioNameInputChange: PropTypes.func,
  onCreateUserPortfolio: PropTypes.func,
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onSaveStock: PropTypes.func,
  onDeleteStock: PropTypes.func,
  onRefreshPortfolio: PropTypes.func,

  portfolioFromHome: PropTypes.object,
  portfolioImageUrlFromHome: PropTypes.string,
  snapShotFnFromHome: PropTypes.func,
  isMobile: PropTypes.bool,
};
